import { Component, OnInit, ViewChild, AfterViewInit, NgModule, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, NavigationEnd, RouterModule, RouterLink  } from '@angular/router';

import { FormArray, FormGroup, FormControl, FormBuilder, AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgFor, NgIf, CommonModule } from '@angular/common';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';

import { Observable, catchError, throwError, map, of } from 'rxjs';
import { tap, filter } from 'rxjs/operators';

import { Event, Partner, User, Station } from '@app/shared/_models/common';
import { FseService, FactureSearcher, FacturePreteLot, StateHelper } from '../fse.service';
import { SpinnerService } from '@app/shared/spinner.service';
import { AlertService } from '@app/shared/alert.service';
import { LoginService } from '@app/common/login.service';
import { ValuesService } from '@app/common/values/values.service';

import * as fselib from 'mercalink-fselib';
import { tofseformat, SupprimerHTTPResponse } from 'mercalink-fselib';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { DetailComponent } from '@app/fse/detail/detail.component'
import { DetailLotComponent } from '@app/fse/detail-lot/detail-lot.component';
import { DossiersScoreComponent } from '@app/fse/dossiers-score/dossiers-score.component';
import * as util from "@app/shared/util";
import * as constants from "@app/shared/constants";
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-paramps',
  imports: [ReactiveFormsModule, FormsModule, RouterModule, MenuCommonComponent, MatButtonModule, MatTableModule, MatIconModule, NgIf, CommonModule, MatSelectModule, MatFormFieldModule, MatInputModule],
  templateUrl: './paramps.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './paramps.component.scss'
})
export class ParampsComponent implements OnInit {
	private area_baseurl?: string = undefined;
  private fseService = inject(FseService);  
  private meklibService: fselib.MercalinkFselibService = inject(fselib.MercalinkFselibService);
  private fb = inject(FormBuilder); // Inject FormBuilder
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private loginService = inject(LoginService);

  paramPS$!: Observable<fselib.ParamPS | undefined>; // Declare paramPS$ to hold the data
  errorMessage: string | undefined; // Declare errorMessage for error handling
  private numIdentFact: string | undefined; // Property to store the numIdentFact
  paramPSForm!: FormGroup; // Declare FormGroup
  isEditing: boolean = false; // Flag to control edit mode
  private originalParamPS: fselib.ParamPS | undefined; // To store original values for cancel

  ngOnInit(): void {
    this.initForm(); // Initialize the form
    this.area_baseurl = this.loginService.area_baseurl;
    if (!this.area_baseurl) {
			this.errorMessage = '[ParampsComponent] Erreur de configuration de la station: area_baseurl vide';
      console.error(this.errorMessage);
      this.paramPS$ = of(undefined); // Ensure observable completes with undefined on error
      return;
    }

    // Get numIdentFact from FseService and then load Paramps
    this.fseService.getNumIdentFact$().subscribe({
      next: (numIdentFact) => {
        this.numIdentFact = numIdentFact;
        if (!this.numIdentFact) {
          this.errorMessage = 'Erreur de configuration: numIdentFact absent pour l\'utilisateur.';
          console.error(this.errorMessage);
          this.paramPS$ = of(undefined);
          return;
        }
        this.loadParamps();
      },
      error: (err) => {
        this.errorMessage = 'Erreur lors de la récupération de l\'identifiant de facturation: ' + err.message;
        console.error(this.errorMessage);
        this.paramPS$ = of(undefined);
      }
    });
    // TEMPLATE Modern way to subscribe with automatic cleanup
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.loadParamps());    
	}

  initForm(): void {
    this.paramPSForm = this.fb.group({
      numIdentFact: [{ value: '', disabled: true }], // numIdentFact might be read-only
      codeSpec: [''],
      typeEmet: [''],
      numEmet: [''],
      mailFact: [''],
      urlSMTP: [''],
      portSMTP: [''],
      sslSMTP: [''], // Added
      authSMTP: [''],
      loginSMTP: [''], // Added
      passSMTP: [''], // Added
      urlPOP3: [''],
      portPOP3: [''],
      loginPOP3: [''],
      passPOP3: [''],
      topCompression: [''],
      topChiffrement: [''],
      topAffichDiagSTS: [''],
      topDesactivSTS: [''],
      topSupprimerNonSV: [''],
      nbMaxFseParLot: [''],

      modeFonc: [''],               // OCT
      numOCT: [''],
      typeDestOCT: [''],
      appliOCT: [''],
      balOCT: [''],

      modeSCOR: [''],               // SCOR

      typeDestinaireARLSCOR: [''],
      codeRegionSCOR: [''],
      tailleMaxPDFSCOR: [''],
      tailleMaxLotSCOR: [''],
    });
    this.paramPSForm.disable(); // Start with form disabled
  }

  loadParamps() {
    if (!this.area_baseurl) {
      // This case should ideally be handled by ngOnInit, but for safety:
      this.errorMessage = '[ParampsComponent] area_baseurl is not set.';
      this.paramPS$ = of(undefined);
      return;
    }
    if (!this.numIdentFact) {
      // This case should ideally be handled by ngOnInit, but for safety:
      this.errorMessage = '[ParampsComponent] numIdentFact is not set.';
      return;
    }    
    this.paramPS$ = this.meklibService.recupererParamPS$(this.area_baseurl, this.numIdentFact).pipe(
      map(response => response?.HTTPResponse?.paramPS), // Extract ParamPS from the response, safely handling undefined
      tap(param => {
        console.log('[loadParamps] ParamPS loaded:', param);
        if (param) { this.paramPSForm.patchValue(param); this.originalParamPS = { ...param }; } // Patch form with loaded data and store original
      }),
      catchError(error => {
        this.errorMessage = 'Failed to load PS parameters: ' + error.message;
        console.error(this.errorMessage, error);
        return of(undefined); // Return an observable of undefined to handle the error gracefully
      })
    );
  }

  toggleEditMode(): void {
    this.isEditing = !this.isEditing;
    if (this.isEditing) {
      this.paramPSForm.enable();
      // numIdentFact should remain disabled
      this.paramPSForm.get('numIdentFact')?.disable();
    } else {
      this.paramPSForm.disable();
      // If cancelling, revert to original values
      if (this.originalParamPS) {
        this.paramPSForm.patchValue(this.originalParamPS);
      }
    }
  }

  saveChanges(): void {
    if (this.paramPSForm.valid && this.area_baseurl && this.numIdentFact) {
      const updatedParamPS: fselib.ParamPS = this.paramPSForm.getRawValue(); // getRawValue includes disabled fields
      console.log('Saving changes:', updatedParamPS);

      (this.meklibService.modifierParamPS as any)(this.area_baseurl, updatedParamPS, this.numIdentFact).pipe(
        tap((response: any) => {
          if (response?.HTTPResponse?.codeRetour === '0') {
            console.log('ParamPS saved successfully:', response);
            this.originalParamPS = { ...updatedParamPS }; // Update original for future cancels
            this.toggleEditMode(); // Exit edit mode
            this.loadParamps(); // Reload the data to reflect changes
          } else {
            const errorMessage = response?.HTTPResponse?.libRetour || 'Erreur inconnue';
            console.error('Failed to save ParamPS:', errorMessage);
            this.errorMessage = `Erreur lors de l'enregistrement des paramètres: ${errorMessage}`;
            // Optionally, show an error notification
          }
        }),
        catchError(error => {
          console.error('Error calling creerParamPS:', error);
          this.errorMessage = `Erreur technique lors de l'enregistrement: ${error.message}`;
          return of(undefined); // Continue observable stream with undefined to prevent breaking
        })
      ).subscribe(); // Don't forget to subscribe!

    } else {
      console.error('Form is invalid. Cannot save.');
      this.errorMessage = 'Veuillez corriger les erreurs du formulaire.';
    }
  }

  cancelEdit(): void {
    this.toggleEditMode(); // This will disable the form and revert values
  }
}
